import React, { useRef } from "react";
import { motion, useInView, useMotionValue, useTransform, useSpring } from "framer-motion";
import "./HealthCare.css";

/* ── animation variants ── */
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.13 },
  }),
};

const slideIn = (dir = "left") => ({
  hidden: { opacity: 0, x: dir === "left" ? -70 : 70, rotateY: dir === "left" ? -15 : 15 },
  show: {
    opacity: 1,
    x: 0,
    rotateY: 0,
    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
  },
});

/* ── 3‑D tilt card wrapper ── */
const TiltImage = ({ src, alt }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-0.5, 0.5], [12, -12]);
  const rotateY = useTransform(x, [-0.5, 0.5], [-12, 12]);
  const springX = useSpring(rotateX, { stiffness: 160, damping: 22 });
  const springY = useSpring(rotateY, { stiffness: 160, damping: 22 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const cx = (e.clientX - rect.left) / rect.width - 0.5;
    const cy = (e.clientY - rect.top) / rect.height - 0.5;
    x.set(cx);
    y.set(cy);
  };
  const handleLeave = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      ref={ref}
      className="healthcare_pg_img_wrapper"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ rotateX: springX, rotateY: springY, transformStyle: "preserve-3d" }}
      whileHover={{ scale: 1.03 }}
    >
      <img src={src} alt={alt} className="healthcare_pg_img" />
      <div className="healthcare_pg_img_shine" />
      <div className="healthcare_pg_img_border" />
    </motion.div>
  );
};

/* ── bullet point ── */
const BulletItem = ({ text, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.li
      ref={ref}
      className="healthcare_pg_bullet"
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
    >
      <span className="healthcare_pg_bullet_dot" />
      <span className="healthcare_pg_bullet_text">{text}</span>
    </motion.li>
  );
};

/* ── section ── */
const Section = ({ imgSrc, imgAlt, imgSide, tag, title, bullets }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const imgLeft = imgSide === "left";

  return (
    <div ref={ref} className={`healthcare_pg_section ${imgLeft ? "healthcare_pg_section--imgLeft" : "healthcare_pg_section--imgRight"}`}>
      {/* Image */}
      <motion.div
        className="healthcare_pg_img_col"
        variants={slideIn(imgLeft ? "left" : "right")}
        initial="hidden"
        animate={inView ? "show" : "hidden"}
      >
        <TiltImage src={imgSrc} alt={imgAlt} />
      </motion.div>

      {/* Text */}
      <motion.div
        className="healthcare_pg_text_col"
        variants={slideIn(imgLeft ? "right" : "left")}
        initial="hidden"
        animate={inView ? "show" : "hidden"}
      >
        <span className="healthcare_pg_tag">{tag}</span>
        <h2 className="healthcare_pg_section_title">{title}</h2>
        <div className="healthcare_pg_divider" />
        <ul className="healthcare_pg_list">
          {bullets.map((b, i) => <BulletItem key={i} text={b} index={i} />)}
        </ul>
      </motion.div>
    </div>
  );
};

/* ── main component ── */
const HealthCare = () => {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });

  const providerBullets = [
    "Significant improvement in revenue cycles due to reduced claim denials.",
    "Faster payments improve cash flow and financial stability.",
    "HIPAA-compliant workflow ensures secure handling of patient data.",
    "Certified coders manage your revenue cycle with precision for maximum reimbursements.",
  ];

  const studentBullets = [
   "Career-focused programs to gain skills for today's job market.",
    "International certification guidance for global career opportunities.",
    "Hands-on training with real claims prepares you for real-world scenarios.",
    "Valuable industry experience through internship opportunities.",
    "Placement support in billing companies and healthcare organizations.",
  ];

  return (
    <div className="healthcare_pg_root">
      {/* Background decoration */}
      <div className="healthcare_pg_bg_grid" />
      <div className="healthcare_pg_orb healthcare_pg_orb--a" />
      <div className="healthcare_pg_orb healthcare_pg_orb--b" />

      <div className="healthcare_pg_inner">
        {/* Header */}
        <motion.div
          ref={headerRef}
          className="healthcare_pg_header"
          initial={{ opacity: 0, y: -40 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="healthcare_pg_eyebrow">Our Advantage</span>
          <h1 className="healthcare_pg_main_title">
            Why Choose{" "}
            <span className="healthcare_pg_main_title--accent">Alphaingen?</span>
          </h1>
          <p className="healthcare_pg_main_sub">
            Empowering healthcare providers and students with precision, compliance, and career excellence.
          </p>
          <div className="healthcare_pg_header_bar" />
        </motion.div>

        {/* Section 1 – Healthcare Providers */}
        <Section
          imgSrc="https://www.fullertonhealth.com/wp-content/uploads/2021/01/Private-Healthcare-Providers-Plays-an-Integral-Role-in-Championing-Access-to-Healthcare.jpg"
          imgAlt="Healthcare Providers"
          imgSide="left"
          tag="For Providers"
          title="Healthcare Providers"
          bullets={providerBullets}
        />

        {/* Separator */}
        <div className="healthcare_pg_sep">
          <span className="healthcare_pg_sep_line" />
          <span className="healthcare_pg_sep_gem" />
          <span className="healthcare_pg_sep_line" />
        </div>

        {/* Section 2 – Students */}
        <Section
          imgSrc="https://alsalamacollege.com/wp-content/uploads/2021/01/642922.jpg"
          imgAlt="Students"
          imgSide="right"
          tag="For Learners"
          title="For Students"
          bullets={studentBullets}
        />
      </div>
    </div>
  );
};

export default HealthCare;